// Enhanced mockExamMapper.js with additional helper functions
// Add these new functions to your existing mockExamMapper.js file

// Existing functions remain unchanged...
export const getExamInfoFromDataUrl = (dataUrl) => {
  // Example: "/data/lessons/telc-b1/lv_part1.json"
  const match = dataUrl.match(/\/data\/lessons\/([^\/]+)\/([^\.]+)\.json/);
  if (!match) return null;

  const [, examKey, lessonType] = match;
  const [provider, level] = examKey.split('-');

  return {
    provider: provider.toUpperCase(),
    level: level.toUpperCase(),
    lessonType,
    examKey
  };
};

export const getMockExamPartId = (lessonType, provider) => {
  const mappings = {
    'lv_part1': 'reading-part-1',
    'lv_part2': 'reading-part-2',
    'lv_part3': 'reading-part-3',
    'lv_part4': 'reading-part-4',
    'lv_part5': 'reading-part-5',
    'sb_part1': 'grammar-part-1',
    'sb_part2': 'grammar-part-2',
    'hv_part1': 'listening-part-1',
    'hv_part2': 'listening-part-2',
    'hv_part3': 'listening-part-3',
    'hv_part4': 'listening-part-4',
    'sa_part1': 'writing-task-1',
    'sa_part2': 'writing-task-1',
    'sa_part3': 'writing-task-3',
    'ma_part1': 'speaking-part-1',
    'ma_part2': 'speaking-part-2',
    'ma_part3': 'speaking-part-3'
  };

  return mappings[lessonType] || null;
};

export const getSectionIdFromLessonType = (lessonType) => {
  if (lessonType.startsWith('lv_')) return 'reading';
  if (lessonType.startsWith('sb_')) return 'grammar';
  if (lessonType.startsWith('hv_')) return 'listening';
  if (lessonType.startsWith('sa_')) return 'writing';
  if (lessonType.startsWith('ma_')) return 'speaking';
  return null;
};

// NEW HELPER FUNCTIONS FOR ENHANCED FUNCTIONALITY

/**
 * Check if a provider/level combination is supported
 * @param {string} provider - The exam provider (TELC, GOETHE, OSD)
 * @param {string} level - The exam level (B1, B2, C1)
 * @returns {boolean} - Whether the combination is supported
 */
export const isSupportedProviderLevel = (provider, level) => {
  const supportedCombinations = {
    'TELC': ['B1', 'B2', 'C1'],
    'GOETHE': ['B1', 'B2', 'C1'],
    'OSD': ['B1', 'B2', 'C1']
  };

  const upperProvider = provider.toUpperCase();
  const upperLevel = level.toUpperCase();

  return supportedCombinations[upperProvider]?.includes(upperLevel) || false;
};

/**
 * Get the expected mock exam file path based on examService structure
 * @param {string} provider - The exam provider
 * @param {string} level - The exam level
 * @param {string} fileName - The specific file name (optional)
 * @returns {string} - The expected file path
 */
export const getMockExamFilePath = (provider, level, fileName = null) => {
  const providerKey = provider.toLowerCase();
  const levelKey = level.toLowerCase();

  if (fileName) {
    return `/data/exams/${providerKey}/${levelKey}/${fileName}`;
  }

  // Default file pattern
  const defaultFileName = `${providerKey}-deutsch-${levelKey}-mock-001.json`;
  return `/data/exams/${providerKey}/${levelKey}/${defaultFileName}`;
};

/**
 * Get a human-readable section name from section ID
 * @param {string} sectionId - The section identifier
 * @param {string} provider - The exam provider for provider-specific naming
 * @returns {string} - Human-readable section name
 */
export const getSectionDisplayName = (sectionId, provider = '') => {
  const sectionNames = {
    'reading': 'Leseverstehen',
    'grammar': provider === 'TELC' ? 'Sprachbausteine' : 'Grammatik',
    'listening': 'Hörverstehen',
    'writing': 'Schriftlicher Ausdruck',
    'speaking': 'Mündlicher Ausdruck'
  };

  return sectionNames[sectionId] || sectionId;
};

/**
 * Validate lesson type format
 * @param {string} lessonType - The lesson type to validate
 * @returns {boolean} - Whether the lesson type format is valid
 */
export const isValidLessonType = (lessonType) => {
  const validPrefixes = ['lv_', 'sb_', 'hv_', 'sa_', 'ma_'];
  const validSuffixes = ['part1', 'part2', 'part3', 'part4', 'part5'];

  if (!lessonType || typeof lessonType !== 'string') return false;

  const hasValidPrefix = validPrefixes.some(prefix => lessonType.startsWith(prefix));
  if (!hasValidPrefix) return false;

  const suffix = lessonType.split('_')[1];
  return validSuffixes.includes(suffix);
};

/**
 * Get all possible mock exam mappings for debugging
 * @returns {object} - Complete mapping structure
 */
export const getAllMockExamMappings = () => {
  return {
    lessonTypeToPartId: {
      'lv_part1': 'reading-part-1',
      'lv_part2': 'reading-part-2',
      'lv_part3': 'reading-part-3',
      'lv_part4': 'reading-part-4',
      'lv_part5': 'reading-part-5',
      'sb_part1': 'grammar-part-1',
      'sb_part2': 'grammar-part-2',
      'hv_part1': 'listening-part-1',
      'hv_part2': 'listening-part-2',
      'hv_part3': 'listening-part-3',
      'hv_part4': 'listening-part-4',
      'sa_part1': 'writing-task-1',
      'sa_part2': 'writing-task-1',
      'sa_part3': 'writing-task-3',
      'ma_part1': 'speaking-part-1',
      'ma_part2': 'speaking-part-2',
      'ma_part3': 'speaking-part-3'
    },
    lessonTypeToSection: {
      'lv_': 'reading',
      'sb_': 'grammar',
      'hv_': 'listening',
      'sa_': 'writing',
      'ma_': 'speaking'
    },
    supportedProviders: ['TELC', 'GOETHE', 'OSD'],
    supportedLevels: ['B1', 'B2', 'C1']
  };
};

/**
 * Debug function to log mock exam loading attempts
 * @param {object} examInfo - The exam information
 * @param {string} lessonType - The lesson type
 * @param {string} filePath - The attempted file path
 * @param {boolean} success - Whether the loading was successful
 */
export const logMockExamAttempt = (examInfo, lessonType, filePath, success) => {
  const debugInfo = {
    timestamp: new Date().toISOString(),
    examInfo,
    lessonType,
    filePath,
    success,
    partId: getMockExamPartId(lessonType, examInfo.provider),
    sectionId: getSectionIdFromLessonType(lessonType),
    isSupported: isSupportedProviderLevel(examInfo.provider, examInfo.level),
    isValidLessonType: isValidLessonType(lessonType)
  };

  console.log('Mock Exam Loading Attempt:', debugInfo);
  return debugInfo;
};